'use client';

import Link from 'next/link';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SessionUser } from '@/components/rbac/AuthSessionProvider';
import { myProfileHref } from '@/hooks/useMyProfileHref';
import { LayoutIcon } from './icons';
import { useHoverTip } from './HoverTip';
import styles from './SidebarFooter.module.css';

interface SidebarFooterProps {
	expanded: boolean;
	user: SessionUser | null;
	status: 'loading' | 'authenticated' | 'unauthenticated';
	loginHref: string;
	onLogout: () => Promise<void>;
	/**
	 * Whether this instance owns the `data-tutorial` anchors. The footer is mounted
	 * twice (desktop aside + mobile panel); only one may carry the anchors or the
	 * spotlight tutorial could target the hidden copy. Defaults to true (desktop).
	 */
	tutorialAnchor?: boolean;
}

interface MenuPosition {
	top: number;
	left: number;
	width: number;
}

const USER_MENU_VIEWPORT_GUTTER = 12;
const USER_MENU_EXPANDED_INSET = 13;
const USER_MENU_EXPANDED_MIN_WIDTH = 248;
const USER_MENU_COLLAPSED_MIN_WIDTH = 220;

const DEFAULT_MENU_POSITION: MenuPosition = {
	top: 0,
	left: 0,
	width: USER_MENU_COLLAPSED_MIN_WIDTH,
};

export function SidebarFooter({
	expanded,
	user,
	status,
	loginHref,
	onLogout,
	tutorialAnchor = true,
}: SidebarFooterProps) {
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState<MenuPosition>(
		DEFAULT_MENU_POSITION,
	);

	// Name shown in the footer: the format-based name from /auth/me, falling back to
	// the email-derived name only if there is no profile yet.
	const displayName = useMemo(
		() => (user ? (user.nombre?.trim() || toDisplayName(user.email)) : 'Visitante'),
		[user],
	);
	// Having a researcher profile is independent of role: an admin may or may not be
	// linked to an investigador. Only expose "Mi perfil" — y "Preferencias", que es
	// una sección de ESE perfil — when there is one.
	const hasProfile = useMemo(() => user?.idInvestigador != null, [user]);
	const initial = useMemo(() => getInitial(displayName), [displayName]);
	// Secondary line: the person's estamento (académico/directivo/…), not the system
	// role. Falls back to the role label when there is no estamento.
	const secondaryLabel = useMemo(
		() => (user ? (user.estamento?.trim() || normalizeRole(user.role)) : 'Visitante'),
		[user],
	);

	const photoUrl = user?.fotoPerfilUrl ?? null;

	// Las dos etiquetas del pie salen sólo con la barra colapsada: desplegada, el
	// nombre y el rol ya están escritos al lado del avatar —y "Iniciar sesión", al
	// lado de su icono—, así que repetirlos flotando sería ruido.
	//
	// La del disparador dice QUIÉN tiene la sesión abierta, no "abrir menú": que esto
	// despliega un menú ya lo anuncia `aria-haspopup`, y el nombre es justamente lo
	// que la barra colapsada esconde. Va también en `aria-label` porque al sacar el
	// `title` este botón se quedaba sin nombre propio: lo habría sacado del texto de
	// adentro, que colapsado mide 0 pero sigue en el DOM.
	const loginTip = useHoverTip(expanded ? null : 'Iniciar sesión', 'right');
	const accountLabel = status === 'loading' ? 'Cargando sesión' : displayName;
	const accountTip = useHoverTip(expanded ? null : accountLabel, 'right');

	useEffect(() => {
		if (!menuOpen) return;

			const updatePosition = () => {
				if (!triggerRef.current) return;

				const rect = triggerRef.current.getBoundingClientRect();
				const horizontalInset = expanded ? USER_MENU_EXPANDED_INSET : 0;
				const baseWidth = expanded
					? Math.max(
							USER_MENU_EXPANDED_MIN_WIDTH,
							rect.width - horizontalInset * 2,
						)
					: USER_MENU_COLLAPSED_MIN_WIDTH;
				const width = Math.min(
					baseWidth,
					window.innerWidth - USER_MENU_VIEWPORT_GUTTER * 2,
				);
				// Mi perfil + Preferencias (ambos dependen de tener perfil) + Cerrar sesión.
				const menuItemsCount = (hasProfile ? 2 : 0) + 1;
			const estimatedHeight = 28 + menuItemsCount * 44;
			const openUpward =
				rect.bottom + estimatedHeight >
				window.innerHeight - USER_MENU_VIEWPORT_GUTTER;

			const top = openUpward
				? Math.max(
						USER_MENU_VIEWPORT_GUTTER,
						rect.top - estimatedHeight - 8,
					)
				: Math.min(
						window.innerHeight - estimatedHeight - USER_MENU_VIEWPORT_GUTTER,
						rect.bottom + 8,
					);
			const desiredLeft = rect.left + horizontalInset;
			const left = Math.min(
				Math.max(USER_MENU_VIEWPORT_GUTTER, desiredLeft),
				window.innerWidth - width - USER_MENU_VIEWPORT_GUTTER,
			);

			setMenuPosition({ top, left, width });
		};

		updatePosition();
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);
		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [menuOpen, expanded, hasProfile]);

	useEffect(() => {
		if (!menuOpen) return;

		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target as Node;
			if (menuRef.current?.contains(target)) return;
			if (triggerRef.current?.contains(target)) return;
			setMenuOpen(false);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, [menuOpen]);

	// Only show the login button once the session RESOLVED with no user. While it is
	// still loading we fall through to the avatar shell (below) with the shimmer, so
	// the button layout never swaps and the shimmer stays continuous.
	if (status !== 'loading' && !user) {
		return (
			<Link
				href={loginHref}
				data-tutorial={tutorialAnchor ? 'home-user' : undefined}
				className={`${styles.sidebarFooterLoginButton} ${
					expanded
						? styles.sidebarFooterLoginButtonExpanded
						: styles.sidebarFooterLoginButtonCollapsed
				}`}
				aria-label="Iniciar sesión"
				{...loginTip.handlers}
			>
				{loginTip.tip}
				<span className={styles.sidebarFooterIconCircle}>
					<LayoutIcon name="user-round" className={styles.sidebarFooterIconAvatar} />
				</span>
				<span
					className={`${styles.sidebarFooterLoginLabelWrap} ${
						expanded
							? styles.sidebarFooterLoginLabelWrapExpanded
							: styles.sidebarFooterLoginLabelWrapCollapsed
					}`}
				>
					<span className={styles.sidebarFooterLoginLabel}>Iniciar sesión</span>
				</span>
			</Link>
		);
	}

	// While loading we render this SAME shell (no user yet) so the layout does not
	// swap when the session resolves — only the avatar shimmer→photo and the name
	// fade in. This kills both the horizontal "jump" and the shimmer restart.
	const isLoading = status === 'loading';

	return (
		<>
			<button
				type="button"
				ref={triggerRef}
				data-tutorial={tutorialAnchor ? 'home-user' : undefined}
				onClick={() => { if (!isLoading) setMenuOpen((prev) => !prev); }}
				className={`${styles.sidebarFooterUserButton} ${
					expanded
						? styles.sidebarFooterUserButtonExpanded
						: styles.sidebarFooterUserButtonCollapsed
				}`}
				aria-haspopup="menu"
				aria-expanded={menuOpen}
				aria-label={accountLabel}
				{...accountTip.handlers}
				>
					{accountTip.tip}
					<span
						className={`${styles.sidebarFooterAvatarSlot} ${
							expanded
								? styles.sidebarFooterAvatarSlotExpanded
								: styles.sidebarFooterAvatarSlotCollapsed
						}`}
					>
						{/* NO key → same element across loading→authenticated so the shimmer
						    is one continuous animation (no remount, no restart). */}
						<SidebarAvatar photoUrl={photoUrl} initial={initial} loading={isLoading} />
					</span>
					<div
						className={`${styles.sidebarFooterIdentity} ${
							expanded
								? styles.sidebarFooterIdentityExpanded
								: styles.sidebarFooterIdentityCollapsed
						}`}
					>
						{isLoading ? (
							<span aria-hidden="true" className={styles.sidebarFooterNameSkeleton} />
						) : (
							<>
								<span className={styles.sidebarFooterName}>{displayName}</span>
								<span className={styles.sidebarFooterRole}>{secondaryLabel}</span>
							</>
						)}
					</div>
					{!isLoading && (
						<LayoutIcon
							name="chevron-up"
							className={`${styles.sidebarFooterChevron} ${
								expanded
									? styles.sidebarFooterChevronExpanded
									: styles.sidebarFooterChevronCollapsed
							} ${menuOpen ? styles.sidebarFooterChevronOpen : ''}`}
						/>
					)}
				</button>

			{menuOpen && !isLoading
				? createPortal(
						<>
							<button
								type="button"
								className={styles.sidebarUserMenuBackdrop}
								aria-label="Cerrar menú de usuario"
								onClick={() => setMenuOpen(false)}
							/>
							<div
								ref={menuRef}
								className={styles.sidebarUserMenuPopover}
								data-tutorial={tutorialAnchor ? 'home-user-menu' : undefined}
								style={{
									top: `${menuPosition.top}px`,
									left: `${menuPosition.left}px`,
									width: `${menuPosition.width}px`,
								}}
								role="menu"
							>
								{hasProfile ? (
									<Link
										href={myProfileHref(user)}
										className={styles.sidebarUserMenuItem}
										onClick={() => setMenuOpen(false)}
									>
										<LayoutIcon
											name="user-round"
											className={styles.sidebarUserMenuIcon}
										/>
										Mi perfil
									</Link>
								) : null}
								{/* Antes acá vivía "Configuración", visible sólo para el admin y
								    apuntando a `/configuracion`, que no es más que un redirect a
								    `/admin/configuraciones` — al que se llega igual desde /admin.
								    El lugar rinde más con las preferencias de la propia cuenta:
								    el enlace lleva al perfil y `?seccion=preferencias` abre el
								    modal solo (lo lee `ProfileActions`). */}
								{hasProfile ? (
									<Link
										href={`${myProfileHref(user)}?seccion=preferencias`}
										className={styles.sidebarUserMenuItem}
										onClick={() => setMenuOpen(false)}
									>
										<LayoutIcon
											name="sliders-horizontal"
											className={styles.sidebarUserMenuIcon}
										/>
										Preferencias
									</Link>
								) : null}
								<button
									type="button"
									className={styles.sidebarUserMenuItem}
									onClick={async () => {
										setMenuOpen(false);
										await onLogout();
									}}
								>
									<LayoutIcon
										name="log-out"
										className={styles.sidebarUserMenuIcon}
									/>
									Cerrar sesión
								</button>
							</div>
						</>,
						document.body,
					)
				: null}
		</>
	);
}

// Avatar with a consistent reveal: ALWAYS show a short shimmer (~600ms) regardless
// of whether the person has a photo, so there is no abrupt flash. The image preloads
// underneath during that window; once the delay is done AND the image has settled
// (loaded or failed), it fades in — or the initial shows if there is no photo.
//
// This SAME element is rendered while the session is still loading (`loading`), so
// the shimmer is one continuous animation from session-load through photo-load —
// it never remounts (no key) and the button shell never swaps, so the circle does
// not jump and the sweep does not restart.
function SidebarAvatar({
	photoUrl,
	initial,
	loading = false,
}: {
	photoUrl: string | null;
	initial: string;
	loading?: boolean;
}) {
	const [photoBroken, setPhotoBroken] = useState(false);
	const [photoLoaded, setPhotoLoaded] = useState(false);
	const [minDelayDone, setMinDelayDone] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setMinDelayDone(true), 600);
		return () => clearTimeout(timer);
	}, []);

	const imageSettled = !photoUrl || photoLoaded || photoBroken;
	// While the session is still resolving, keep shimmering — do not resolve yet.
	const revealed = !loading && minDelayDone && imageSettled;
	const showPhoto = revealed && Boolean(photoUrl) && !photoBroken;

	return (
		<div className={styles.sidebarFooterAvatar} style={{ position: 'relative', overflow: 'hidden' }}>
			{photoUrl && !photoBroken && (
				// eslint-disable-next-line @next/next/no-img-element
				<img
					src={photoUrl}
					alt=""
					onLoad={() => setPhotoLoaded(true)}
					onError={() => setPhotoBroken(true)}
					style={{
						position: 'absolute',
						inset: 0,
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						borderRadius: '50%',
						display: 'block',
						opacity: showPhoto ? 1 : 0,
						transition: 'opacity 0.3s ease',
					}}
				/>
			)}
			{!revealed && <span aria-hidden="true" className={styles.sidebarFooterAvatarSkeleton} />}
			{revealed && !showPhoto && initial}
		</div>
	);
}

function normalizeRole(role: string) {
	if (role === 'admin') return 'Admin';
	if (role === 'editor') return 'Editor';
	if (role === 'investigador') return 'Investigador';
	return role;
}

function toDisplayName(email: string) {
	const raw = email.split('@')[0] || 'usuario';
	const words = raw
		.replace(/[._-]+/g, ' ')
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2);

	if (!words.length) return 'Usuario';
	return words
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

function getInitial(value: string) {
	return value.charAt(0).toUpperCase();
}
